type MutationMethod = "POST" | "PATCH" | "PUT" | "DELETE";

export function apiClient<T = unknown>(url: string, options?: RequestInit): Promise<T>;
/** Mutation overload — returns `T | null` to account for 204 No Content responses. */
export function apiClient<T = unknown>(
  url: string,
  options: RequestInit & { method: MutationMethod }
): Promise<T | null>;

/**
 * Shared API client for making fetch requests with consistent error handling.
 *
 * Features:
 * - Handles cross-origin and same-origin redirects
 * - Parses JSON error responses (with fallback for non-JSON error bodies like HTML 504s)
 * - Handles 204 No Content
 * - Throws errors with `.status` for use with the global error handler
 */
export async function apiClient<T = unknown>(
  url: string,
  options?: RequestInit
): Promise<T | null> {
  const res = await fetch(url, {
    ...options,
    redirect: "manual",
  });

  // handle cross origin redirects
  if (res.type === "opaqueredirect") {
    const parsedUrl = new URL(res.url);
    const strippedUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}${
      parsedUrl.port ? `:${parsedUrl.port}` : ""
    }`;
    window.location.href = strippedUrl;
    // Page is being redirected anyway, so return a promise that never resolves since no value should ever be consumed anyway.
    return new Promise<T>(() => {});
  }

  // handle same origin redirects by following them
  if ([301, 302, 307, 308].includes(res.status)) {
    const redirected = await fetch(url, { ...options, redirect: "follow" });
    if (!redirected.ok) {
      const error: any = new Error();
      error.status = redirected.status;
      try {
        const resp = await redirected.json();
        error.message = JSON.stringify(resp);
      } catch {
        const text = await redirected.text().catch(() => "");
        error.message = text || `Request failed with status ${redirected.status}`;
      }
      throw error;
    }
    return redirected.json();
  }

  if (!res.ok) {
    const error: any = new Error();
    error.status = res.status;
    try {
      const resp = await res.json();
      error.message = JSON.stringify(resp);
    } catch {
      const text = await res.text().catch(() => "");
      error.message = text || `Request failed with status ${res.status}`;
    }
    throw error;
  }

  // Handle 204 No Content responses
  if (res.status === 204) {
    return null;
  }

  return res.json();
}

/**
 * Convenience wrapper for mutation requests (POST/PATCH/PUT/DELETE).
 * Automatically sets Content-Type and JSON-serializes the body.
 *
 * Use this for mutations in event handlers or standalone API modules where
 * React hooks can't be used. For declarative data fetching in components,
 * prefer `useFetcher` or `useMutator` from ~/hooks/useFetcher.
 */
export const apiMutate = async <T = unknown>(
  url: string,
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  body?: unknown,
  options?: Omit<RequestInit, "method" | "body">
): Promise<T | null> => {
  const init: RequestInit = {
    method,
    ...options,
  };

  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.headers = {
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    };
    init.body = JSON.stringify(body);
  }

  return apiClient<T>(url, init);
};
